import { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import Siema from 'siema';
import { useIntl } from 'gatsby-plugin-react-intl';

import RichText, { fragment } from '../../../Core/RichText';
import IndividualBlade from '../../IndividualBlades';
import { useInterval } from '../../../../utils';
import { ResponsiveContext } from '../../../Providers/Responsive';

import {
  StyledContainer,
  Heading,
  Grid,
  CarouselControls,
  Footer,
  Btn,
  StyledPhone,
  CarouselButton,
  PausePlayBtn,
} from './styles';

// * heading {Obj/Richtext} - Richtext node for the card headline
// * blocks {array} - array of data objs for each card / block

let timeOut;

const startIndex = 0;

const PCMACarousel = ({ data }) => {
  if (!data) return null;
  const {
    id,
    component,
    cards,
    ctas,
    CtaMessage,
    elements,
    sectionContainerHeading,
    backgroundColor,
    contentAlignment,
  } = data;

  const intl = useIntl();
  const carouselItems = cards || elements;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliding, setSliding] = useState(false);

  const { mobile } = useContext(ResponsiveContext);
  const carousel = useRef(null);
  const numOfItems = elements.length;

  // * Function to change the slide
  const changeSlide = index => {
    carousel.current.goTo(index);
    setSliding(false);
  };

  const initCarousel = () => {
    const instance = new Siema({
      selector: `.siema-${id}`,
      duration: 350,
      easing: 'ease-out',
      perPage: 1,
      startIndex,
      draggable: true,
      multipleDrag: true,
      threshold: 20,
      loop: true,
      rtl: false,
      onInit: () => {
        updateRoles();
      },
      onChange: () => {
        updateRoles();
        setCurrentSlide(Math.ceil(carousel.current.currentSlide));
      },
    });

    return instance;
  };

  const destroyCarousel = () => {
    if (carousel.current) carousel.current.destroy(true);
  };

  const updateRoles = () => {
    setTimeout(() => {
      const carouselWrapper = document.querySelector('.carousel-wrapper');
      const listTarget = carouselWrapper.querySelector(
        '.carousel-wrapper > div',
      );
      const listItemTarget = carouselWrapper.querySelectorAll(
        '.carousel-wrapper > div > div',
      );

      listTarget.setAttribute('role', 'list');

      for (let i = 0; i < listItemTarget.length; i += 1) {
        listItemTarget[i].setAttribute('role', 'listitem');
      }
    }, 1000);
  };

  useEffect(() => {
    window.addEventListener('resize', updateRoles);
  });

  const updateResponsiveView = responsive => {
    if (responsive) {
      carousel.current = initCarousel();
      setSliding(true);
    } else {
      setSliding(false);
      destroyCarousel();
    }
  };

  useInterval(
    () => {
      if (mobile) {
        carousel.current.next();
        if (currentSlide + 1 < numOfItems) {
          setCurrentSlide(currentSlide + 1);
        } else {
          setCurrentSlide(0);
        }
      }
    },
    sliding ? 10000 : null,
  );

  const toggle = () => {
    setSliding(!sliding);
  };

  useEffect(() => {
    updateResponsiveView(mobile);

    return () => {
      window.clearTimeout(timeOut);
    };
  }, [mobile]);

  return (
    <StyledContainer className={component} backgroundColor="transparent">
      {sectionContainerHeading && (
        <Heading className={contentAlignment}>
          <RichText document={sectionContainerHeading} />
        </Heading>
      )}
      <Grid className={`siema-${id} carousel-wrapper`}>
        {carouselItems.map((product, indx) => {
          return (
            <StyledPhone
              key={product.id}
              data={product}
              index={indx + 1}
              component={component}
              backgroundColor={backgroundColor}
              showNumber={false}
              className="pc-money-step"
            />
          );
        })}
      </Grid>
      {mobile && (
        <CarouselControls>
          <ul className="no-bullets">
            {elements.map((el, idx) => {
              return (
                <li key={el.id}>
                  <CarouselButton
                    type="button"
                    className={currentSlide === idx ? 'active' : ''}
                    onClick={() => changeSlide(idx)}
                  >
                    <span className="sr-only">{`${intl.formatMessage({
                      id: 'stepLabel',
                    })} ${idx}`}</span>
                  </CarouselButton>
                </li>
              );
            })}
            <PausePlayBtn onClick={() => toggle()}>
              {sliding ? (
                <>
                  <svg
                    width="13"
                    height="15"
                    viewBox="0 0 13 15"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <title>
                      {intl.formatMessage({
                        id: 'pause',
                      })}
                    </title>
                    <rect width="4.33333" height="15" fill="currentColor" />
                    <rect
                      x="8.66602"
                      width="4.33333"
                      height="15"
                      fill="currentColor"
                    />
                  </svg>
                  <span>
                    {intl.formatMessage({
                      id: 'pause',
                    })}
                  </span>
                </>
              ) : (
                <>
                  <svg
                    width="13"
                    height="15"
                    viewBox="0 0 13 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <title>
                      {intl.formatMessage({
                        id: 'play',
                      })}
                    </title>
                    <path
                      d="M13 7.5L0.249999 14.8612L0.25 0.138783L13 7.5Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>
                    {intl.formatMessage({
                      id: 'play',
                    })}
                  </span>
                </>
              )}
            </PausePlayBtn>
          </ul>
        </CarouselControls>
      )}
      {(CtaMessage || ctas) && (
        <Footer>
          {CtaMessage && <h3>{CtaMessage}</h3>}
          {ctas &&
            ctas.map(cta => {
              if (cta.hideFrench === true) return null;
              return cta.internal.type === 'ContentfulBlade' ? (
                <IndividualBlade key={cta.id} data={cta} />
              ) : (
                <Btn key={cta.id} data={cta}>
                  <RichText document={cta.label} options={fragment} />
                </Btn>
              );
            })}
        </Footer>
      )}
    </StyledContainer>
  );
};

PCMACarousel.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default PCMACarousel;
